<template>
  <div>
    <div
      v-show="loaded && !noMapToDisplay"
      id="map-wrapper"
      :class="searchMode ? 'mag-cursor' : ''"
    >
      <!-- parcel selection tool instructions -->
      <div v-if="searchMode" id="parcel-selection-instructions">
        drag map to find a parcel, click parcel to view its information
      </div>
      <!-- parcel selection tool icon -->
      <a
        class="magnify-glass-icon-button esri-widget--button esri-widget esri-interactive"
        :class="searchMode ? 'map-search-mode-on' : ''"
        id="parcel-selection-tool-button"
        title="Parcel Selection Tool"
        @click="searchMode = !searchMode"
        ><img
          alt="magnifying glass icon"
          :src="baseUrl + 'images/icons/magnifying-glass.png'"
          width="21"
      /></a>
      <!-- map -->
      <div id="map" ref="map" v-on:selectparcel="selectParcel" />
    </div>
    <div
      v-show="!loaded && !noMapToDisplay"
      id="map-wrapper-skeleton"
      class="bg-gray placeholder-glow placeholder"
    ></div>
  </div>
</template>

<style scoped>
.magnify-glass-icon-button {
  box-shadow: 0 1px 2px rgb(0 0 0 / 30%);
  margin: 88px 0 0px 16px;
  position: absolute;
  z-index: 1;
}
.magnify-glass-icon-button:hover {
  background-color: #7dbc76;
}
.map-search-mode-on {
  background-color: #bbfab1;
}
.mag-cursor {
  /* cursor: url("/images/icons/magnifying-glass.png"), auto; */
  cursor: crosshair;
}
#map,
#map-wrapper,
#map-wrapper-skeleton {
  height: 100% !important;
  min-height: 300px;
  max-height: 600px;
  width: 100% !important;
}
#parcel-selection-instructions {
  background-color: #bbfab1;
  margin: 15px 0 0 47px;
  opacity: 0.78;
  padding: 2px 16px;
  position: absolute;
  text-align: center;
  z-index: 1;
}
</style>

<script>
/*
  ArcGIS API for JavaScript Docs
  https://developers.arcgis.com/javascript/
*/
import { setDefaultOptions, loadModules } from "esri-loader";

export default {
  data() {
    return {
      baseUrl: process.env.BASE_URL,
      loaded: false,
      noMapToDisplay: false,
      map: null,
      mapGraphic: null,
      mapGraphicsLayerClass: null,
      mapSpatialReference: null,
      mapView: null,
      mapViewClass: null,
      parcelCenterCoordinates: null,
      parcelBorderPolygon: null,
      searchMode: false,
    };
  },
  emits: ["parcelMapped"],
  methods: {
    getPolygonCenter(arr) {
      var minX, maxX, minY, maxY;
      for (var i = 0; i < arr.length; i++) {
        minX = arr[i][0] < minX || minX == null ? arr[i][0] : minX;
        maxX = arr[i][0] > maxX || maxX == null ? arr[i][0] : maxX;
        minY = arr[i][1] < minY || minY == null ? arr[i][1] : minY;
        maxY = arr[i][1] > maxY || maxY == null ? arr[i][1] : maxY;
      }
      return [(minX + maxX) / 2, (minY + maxY) / 2];
    },
    createMap() {
      setDefaultOptions({ version: "4.17" });
      loadModules(
        [
          "esri/Map",
          "esri/Basemap",
          "esri/Graphic",
          "esri/layers/GraphicsLayer",
          "esri/views/MapView",
          "esri/geometry/SpatialReference",
          "esri/layers/MapImageLayer",
        ],
        {
          css: true,
        }
      ).then(
        ([
          Map,
          Basemap,
          Graphic,
          GraphicsLayer,
          MapView,
          SpatialReference,
          MapImageLayer,
        ]) => {
          const mapImageLayer = new MapImageLayer({
            url: "https://gis.acimap.us/services/rest/services/BaseMaps/Topographic_BaseMap/MapServer",
            spatialReference: { wkid: 2244 },
          });
          const basemap = new Basemap({
            baseLayers: [mapImageLayer],
          });
          this.map = new Map({
            basemap: basemap,
          });
          this.mapSpatialReference = new SpatialReference({ wkid: 2244 });
          this.mapGraphic = new Graphic({});
          this.mapGraphic.symbol = {
            color: [0, 255, 1, 0.25],
            outline: {
              color: [0, 128, 0, 0.5],
              width: 1,
            },
            type: "simple-fill",
          };
          this.map.layers.push(
            new GraphicsLayer({
              graphics: [this.mapGraphic],
            })
          );
          this.mapView = new MapView({
            container: this.$refs.map,
            map: this.map,
            zoom: 6,
          });
          this.mapView.when(
            () => {
              this.showParcel(
                this.parcelCenterCoordinates,
                this.parcelBorderPolygon
              );
              this.$emit("parcelMapped", true);
              this.loaded = true;
            },
            (error) => {
              console.error(error);
              this.loaded = false;
              this.noMapToDisplay = true;
              this.$emit("parcelMapped", false);
            }
          );
          const that = this;
          this.mapView.on("click", function(event) {
            const pstClass = document
              .getElementById("parcel-selection-tool-button")
              .getAttribute("class");
            const parcelSelectActive =
              pstClass.search("map-search-mode-on") > 0;
            if (parcelSelectActive) {
              that.selectParcel(event);
            }
          });
        }
      );
    },
    getMapData(key, pst = false) {
      this.axios
        .get(
          this.restApiUrl + "services/QueryLayers/QueryLayers/MapServer/find",
          {
            params: {
              contains: false,
              f: "json",
              layers: "0,10",
              returnGeometry: true,
              searchFields: "PIN",
              searchText: key,
            },
          }
        )
        .then((response) => {
          try {
            if (response.data.results.length) {
              // parcelPoly
              const parcelPoly = response.data.results.find(
                (layer) => layer.layerName == "Parcel_Poly"
              );
              if (parcelPoly) {
                let ppxy = this.getPolygonCenter(parcelPoly.geometry.rings[0]);
                this.parcelCenterCoordinates = {
                  x: ppxy[0],
                  y: ppxy[1],
                };
                this.parcelBorderPolygon = parcelPoly.geometry.rings;
              } else {
                // address
                const address = response.data.results.find(
                  (layer) => layer.layerName == "Addresses"
                );
                if (address) {
                  this.parcelCenterCoordinates = address.geometry;
                  this.parcelBorderPolygon = [];
                }
              }
              if (pst) {
                // parcel selected with Parcel Selection Tool
                this.showParcel(
                  this.parcelCenterCoordinates,
                  this.parcelBorderPolygon
                );
              }
              this.$emit("parcelMapped", true);
              this.loaded = true;
            } else {
              console.log("failed to lookup address at acimap");
              this.$emit("parcelMapped", false);
              this.loaded = false;
              this.noMapToDisplay = true;
            }
          } catch (error) {
            console.error("error loading map", error);
            this.$emit("parcelMapped", false);
            this.loaded = false;
            this.noMapToDisplay = true;
          }
        });
    },
    showParcel(coordinates, parcelPolyRings) {
      if (this.mapView) {
        this.mapView.when(() => {
          this.mapView.center = {
            x: coordinates.x,
            y: coordinates.y,
            spatialReference: {
              wkid: 2244,
            },
          };

          this.mapView.scale = 3000;
          this.mapGraphic.geometry = {
            rings: parcelPolyRings,
            spatialReference: this.mapSpatialReference,
            type: "polygon",
          };
        });
      }
    },
    selectParcel(event) {
      this.axios
        .get(this.county.mapping.arcgis.querylayer_url, {
          params: {
            f: "pjson",
            geometry: event.mapPoint.x + "," + event.mapPoint.y,
            geometryType: "esriGeometryPoint",
            returnCountOnly: false,
            returnDistinctValues: false,
            returnGeometry: true,
            returnIdsOnly: false,
            returnM: false,
            returnZ: false,
            spatialRel: "esriSpatialRelIntersects",
          },
        })
        .then((response) => {
          this.axios
            .get(process.env.VUE_APP_DATASERVICE + "GetDuplicateNumber", {
              params: {
                corpCode: this.county.corpCode,
                payYear: this.currentPayYear,
                unformattedStateKey:
                  response.data["features"][0]["attributes"]["PIN"],
              },
            })
            .then((response) => {
              this.$router.push({
                name: "parcel",
                params: {
                  dup: response.data,
                  payYear: this.currentPayYear,
                },
              });
            });
        });
    },
  },
  mounted() {
    this.createMap();
    this.getMapData(this.parcel.UNFORMATEDSTATEKEY);
  },
  props: {
    county: {
      type: Object,
      required: true,
    },
    currentPayYear: {
      type: Number,
      required: true,
    },
    restApiUrl: {
      type: String,
      required: true,
    },
    parcel: {
      type: Object,
      required: true,
    },
  },
  watch: {
    "parcel.UNFORMATEDSTATEKEY": function(val) {
      this.getMapData(val, true);
    },
  },
};
</script>
